/**
 * SOLID Review
 * S — Strong. One concern: which of the Monetag banner's calls to action shows, rotated every 60 s in active play and every 30 s
 *     otherwise, with a short fade between two.
 * O — Good. A new call to action is one more entry the view lists.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The index and the fade; the view's one input.
 * D — Strong. The timers.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IServiceViewTimers } from './platform/IConfigDepViewPlatform';
import { GamePhase } from '../../application/ports/ClientVocabulary';

/** The phases of active play: the slower rotation then. */
const ACTIVE_GAMEPLAY_PHASES: ReadonlySet<GamePhase> = Object.freeze(new Set<GamePhase>([
  GamePhase.ROLLING, GamePhase.PEEKING, GamePhase.CLAIMING, GamePhase.DECIDING, GamePhase.REVEALING,
]));
/** The fade between two calls to action. */
const FADE_MS = 250;

export interface IStateTopBannerAd {
  readonly index: number;
  readonly visible: boolean;
}

/** What the banner's view hands it: the game's phase. */
type TBannerInputs = readonly [currentPhase: GamePhase];

const FIRST: IStateTopBannerAd = { index: 0, visible: true };
const FADED: Partial<IStateTopBannerAd> = { visible: false };

/**
 * THE MONETAG BANNER'S ROTATION (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/TopBannerAd.tsx`'s state and
 * effect: every interval the banner fades out, and a moment later shows the next call to action; the interval follows the phase.
 */
export class TopBannerAdViewModel implements IServiceViewModel<IStateTopBannerAd> {
  private readonly state = new ObservableState<IStateTopBannerAd>(FIRST);
  private readonly reactions = new Reactions<TBannerInputs>([{
    deps: (inputs) => inputs,
    run: ([currentPhase]) => {
      const timer = this.timers.setInterval(() => {
        this.state.update(FADED);
        this.timers.setTimeout(() => {
          const next: Partial<IStateTopBannerAd> = { index: (this.state.get().index + 1) % this.count, visible: true };
          this.state.update(next);
        }, FADE_MS);
      }, TopBannerAdViewModel.intervalOf(currentPhase));
      return () => this.timers.clearInterval(timer);
    },
  }]);

  constructor(
    private readonly timers: IServiceViewTimers,
    private readonly count: number,
  ) {}

  static intervalOf(phase: GamePhase): number {
    return ACTIVE_GAMEPLAY_PHASES.has(phase) ? 60_000 : 30_000;
  }

  getSnapshot(): IStateTopBannerAd {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TBannerInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TBannerInputs);
  }
}
